/* eslint-disable max-lines */
import { LoadingButton } from '@mui/lab';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  debounce,
  every,
  filter,
  find,
  includes,
  isBoolean,
  isEmpty,
  map,
  noop,
  some,
  uniq,
  without,
} from 'lodash';
import Lottie from 'lottie-react';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  bulkFillApi,
  generateBulkLabelsApi,
  markLineItemsPrintedApi,
  regenerateLabelsApi,
} from 'api/requests';
import Download from 'assets/illustrations/downloadIcon.json';
import DisabledButtonTooltip from 'common/components/DisabledButtonTooltip';
import LoadingModule from 'common/components/LoadingModule';
import useRoles from 'common/hooks/useRoles';
import useWebSocket from 'common/hooks/useWebSocket';
import { EnableDispenseFlow, EnableInventoryLookup } from 'common/utils';
import BulkLotNdcReviewDialog from 'modules/WorkQueue/components/BulkLotNdcReviewDialog';
import { LABEL_BUILD } from 'modules/WorkQueue/utils/bulkLotNdcHelperText';
import { format } from 'date-fns';
import lineItemEnum from 'enums/lineItem.enum';
import {
  ClientPrintJob,
  DefaultPrinter,
  FileSourceType,
  JSPrintManager,
  PrintFilePDF,
  Sizing,
  WSStatus,
} from 'jsprintmanager';
import { useParams } from 'react-router-dom';
import { handleError } from 'store/thunks/errorHandlerThunks';
import PrintResultSummary from 'common/components/PrintResultSummary';
import {
  REGENERATE_LOCKED_BY_FILL_MESSAGE,
  REGENERATING_LABELS_MESSAGE,
} from 'common/constants/labelMessages';
import useLabelRegenerationProgress from 'common/hooks/useLabelRegenerationProgress';
import useInfoDialog from './useInfoDialog';

/**
 * Which terminal action the bulk toolbar button and the review dialog's bottom bar offer.
 *
 * Both buttons run the SAME component (BulkLabelActionButton below): the Review Lot & NDC popup,
 * the PDF build, the JSPM print and the regenerate machinery are identical whichever one opened
 * it, and duplicating them into a second component would leave two copies of the print lock to
 * keep in step. The mode changes exactly two things — the label and count on the toolbar button,
 * and which control sits at the end of the bar.
 */
const BULK_ACTION_MODE = {
  PRINT: 'PRINT',
  FILL: 'FILL',
};

const PrintLabelContext = createContext({});

const PrintLabelProvider = ({
  initialValue,
  children,
  lockedByOtherUsers = [],
  lockedItemsUserMap = {},
  lineItems = [],
}) => {
  const [labelItemIds, setLabelItemIds] = useState(initialValue);
  const [userUnselectedItems, setUserUnselectedItems] = useState(new Set());
  const { isConnected, sendMessage } = useWebSocket();
  const clearedItemsRef = useRef(new Set());
  const previousInitialValueRef = useRef(initialValue);
  const labelItemIdsRef = useRef(labelItemIds);
  const serverLockedItemsRef = useRef([...initialValue]); // Track what's been sent to server

  const debouncedSyncLockState = useMemo(
    () =>
      debounce(() => {
        if (!isConnected) return;

        const currentItems = labelItemIdsRef.current;
        const serverItems = serverLockedItemsRef.current;

        const itemsToLock = currentItems.filter((id) => !includes(serverItems, id));

        const itemsToUnlock = serverItems.filter((id) => !includes(currentItems, id));

        const sessionId = sessionStorage.getItem('socketSessionId');

        if (!isEmpty(itemsToLock)) {
          sendMessage('lineItems:lock', {
            lineItemIds: itemsToLock,
            sessionId,
          });
        }

        if (!isEmpty(itemsToUnlock)) {
          sendMessage('lineItems:unlock', {
            lineItemIds: itemsToUnlock,
            sessionId,
          });
        }

        serverLockedItemsRef.current = [...currentItems];
      }, 1000),
    [isConnected, sendMessage]
  );

  const updateLabelItemIds = useCallback(
    ({ isSelected, labelItemId }) => {
      setLabelItemIds((currentLabelItemIds) => {
        const updated = isSelected
          ? [...currentLabelItemIds, labelItemId]
          : without(currentLabelItemIds, labelItemId);

        if (!isSelected) {
          setUserUnselectedItems((prevUnselected) => new Set([...prevUnselected, labelItemId]));
          clearedItemsRef.current.add(labelItemId);
        } else {
          setUserUnselectedItems((prevUnselected) => {
            const newSet = new Set(prevUnselected);
            newSet.delete(labelItemId);
            return newSet;
          });
          clearedItemsRef.current.delete(labelItemId);
        }

        labelItemIdsRef.current = updated;

        if (isConnected) {
          debouncedSyncLockState();
        }

        return updated;
      });
    },
    [debouncedSyncLockState, isConnected]
  );

  const clearLabelItems = useCallback(() => {
    if (debouncedSyncLockState && typeof debouncedSyncLockState.flush === 'function') {
      debouncedSyncLockState.flush();
    }
    if (debouncedSyncLockState && typeof debouncedSyncLockState.cancel === 'function') {
      debouncedSyncLockState.cancel();
    }

    setLabelItemIds((currentLabelItemIds) => {
      // Emit unlock message for all currently selected items before clearing
      if (isConnected && !isEmpty(currentLabelItemIds)) {
        sendMessage('lineItems:unlock', {
          lineItemIds: [...currentLabelItemIds],
          sessionId: sessionStorage.getItem('socketSessionId'),
        });
        serverLockedItemsRef.current = [];
      }

      setUserUnselectedItems((prevUnselected) => {
        const newSet = new Set(prevUnselected);
        currentLabelItemIds.forEach((id) => newSet.add(id));
        return newSet;
      });

      currentLabelItemIds.forEach((id) => clearedItemsRef.current.add(id));

      labelItemIdsRef.current = [];

      return [];
    });
  }, [isConnected, sendMessage, debouncedSyncLockState]);

  const memoizedValue = useMemo(
    () => ({
      labelItemIds,
      updateLabelItemIds,
      clearLabelItems,
      setLabelItemIds,
      isConnected,
      debouncedSyncLockState,
      setUserUnselectedItems,
      lockedByOtherUsers,
      lockedItemsUserMap,
      labelItemIdsRef,
      lineItems,
    }),
    [
      labelItemIds,
      clearLabelItems,
      updateLabelItemIds,
      setLabelItemIds,
      isConnected,
      debouncedSyncLockState,
      setUserUnselectedItems,
      lockedByOtherUsers,
      lockedItemsUserMap,
      lineItems,
    ]
  );

  useEffect(() => {
    const previousItems = previousInitialValueRef.current;
    const expiredItems = previousItems.filter((id) => !includes(initialValue, id));

    if (expiredItems.length > 0) {
      setUserUnselectedItems((prevUnselected) => {
        const newSet = new Set(prevUnselected);
        expiredItems.forEach((id) => newSet.add(id));
        return newSet;
      });
      expiredItems.forEach((id) => clearedItemsRef.current.add(id));
    }

    setLabelItemIds((currentLabelItemIds) => {
      const newItemsFromInitial = initialValue.filter(
        (id) =>
          !includes(currentLabelItemIds, id) &&
          !userUnselectedItems.has(id) &&
          !clearedItemsRef.current.has(id)
      );

      const updated = [...currentLabelItemIds, ...newItemsFromInitial];
      labelItemIdsRef.current = updated;

      return updated;
    });

    previousInitialValueRef.current = initialValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  useEffect(() => {
    labelItemIdsRef.current = labelItemIds;
  }, [labelItemIds]);

  useEffect(() => {
    if (isEmpty(labelItemIds) || isEmpty(lockedByOtherUsers)) return;

    const itemsToDeselect = labelItemIds.filter((itemId) => includes(lockedByOtherUsers, itemId));

    if (itemsToDeselect.length > 0) {
      setLabelItemIds((prev) => {
        const updated = prev.filter((id) => !includes(itemsToDeselect, id));
        labelItemIdsRef.current = updated;
        return updated;
      });

      setUserUnselectedItems((prevUnselected) => {
        const newSet = new Set(prevUnselected);
        itemsToDeselect.forEach((id) => newSet.add(id));
        return newSet;
      });

      itemsToDeselect.forEach((id) => clearedItemsRef.current.add(id));

      if (isConnected) {
        const sessionId = sessionStorage.getItem('socketSessionId');
        sendMessage('lineItems:unlock', {
          lineItemIds: itemsToDeselect,
          sessionId,
        });

        serverLockedItemsRef.current = serverLockedItemsRef.current.filter(
          (id) => !includes(itemsToDeselect, id)
        );
      }
    }
  }, [lockedByOtherUsers, labelItemIds, isConnected, sendMessage]);

  useEffect(() => {
    return () => {
      if (debouncedSyncLockState && typeof debouncedSyncLockState.flush === 'function') {
        debouncedSyncLockState.flush();
      }
      if (debouncedSyncLockState && typeof debouncedSyncLockState.cancel === 'function') {
        debouncedSyncLockState.cancel();
      }

      const currentItems = labelItemIdsRef.current;
      if (isConnected && !isEmpty(currentItems)) {
        sendMessage('lineItems:unlock', {
          lineItemIds: [...currentItems],
          sessionId: sessionStorage.getItem('socketSessionId'),
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <PrintLabelContext.Provider value={memoizedValue}>{children}</PrintLabelContext.Provider>;
};

PrintLabelProvider.propTypes = {
  initialValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  lockedByOtherUsers: PropTypes.arrayOf(PropTypes.string),
  lockedItemsUserMap: PropTypes.shape({}),
  lineItems: PropTypes.arrayOf(PropTypes.shape({})),
};

PrintLabelProvider.defaultProps = {
  lockedByOtherUsers: [],
  lockedItemsUserMap: {},
  lineItems: [],
};

const usePrintLabelContext = () => useContext(PrintLabelContext);

// `isLocked` only freezes the box (e.g. while its screen refreshes) and, unlike `isDisabled`, keeps
// it showing the selection — the item is still printable, just not editable right now.
const PrintLabelCheckBox = ({
  lineItemId,
  isDisabled,
  isLocked,
  isLabelCreated,
  disabledReason,
}) => {
  const { labelItemIds, updateLabelItemIds, lockedByOtherUsers } = usePrintLabelContext();

  const onChangeHandler = (event) => {
    const isSelected = event.target.checked;
    const labelItemId = event.target.id;

    updateLabelItemIds({ isSelected, labelItemId });
  };

  const getTooltipTitle = () => {
    if (includes(lockedByOtherUsers, lineItemId)) {
      return '';
    }

    if (disabledReason) {
      return disabledReason;
    }

    if (isDisabled && isLabelCreated) {
      return 'Product in review';
    }
    if (isBoolean(isLabelCreated) && !isLabelCreated) {
      return 'Label Creation In Progress';
    }
    return '';
  };

  const isLockedByOtherUser = includes(lockedByOtherUsers, lineItemId);
  const isChecked = isDisabled ? false : includes(labelItemIds, lineItemId);

  return (
    <Tooltip title={getTooltipTitle()} placement='top' arrow>
      <span>
        <Checkbox
          id={lineItemId}
          inputProps={{ 'aria-label': `mark ${lineItemId} to be printed` }}
          size='small'
          sx={{ py: 0 }}
          checked={isChecked}
          onChange={onChangeHandler}
          disabled={isDisabled || isLocked || isLockedByOtherUser}
        />
      </span>
    </Tooltip>
  );
};

PrintLabelCheckBox.propTypes = {
  lineItemId: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isLocked: PropTypes.bool,
  isLabelCreated: PropTypes.bool,
  disabledReason: PropTypes.string,
};

PrintLabelCheckBox.defaultProps = {
  isDisabled: false,
  isLocked: false,
  isLabelCreated: undefined,
  disabledReason: undefined,
};

const BulkLabelActionButton = ({
  mode,
  onSuccess,
  onWorkQueue,
  isBulk,
  isBatchShipment,
  isTransferMode,
  disabled,
}) => {
  const isFillMode = mode === BULK_ACTION_MODE.FILL;
  const workQueueState = useSelector(({ workQueue }) => workQueue);
  const preset = workQueueState?.preset || '';
  const bulkLabelsQueue = workQueueState[preset] || '';
  const pages = bulkLabelsQueue?.pages || {};
  const currentPage = bulkLabelsQueue?.currentPage || 1;
  const items = pages[currentPage] || [];
  const inputFilter = bulkLabelsQueue?.filter;
  const lockedInventoryItems = useSelector(({ workQueue }) => workQueue.lockedInventoryItems);

  const { mpi } = useParams();

  const allLineItems = useSelector(({ patient }) => {
    const orderHistory = patient[mpi]?.orderHistory ?? [];
    const orders = patient[mpi]?.orders ?? [];
    return [...orderHistory, ...orders].flatMap((order) => order.lineItems ?? []);
  });

  const hasAccess = useRoles();
  const { labelItemIds, clearLabelItems, lineItems: contextLineItems } = usePrintLabelContext();
  const [open, setOpen] = useState(false);
  const [bulkLabelUrl, setBulkLabelUrl] = useState(null);
  // POM-6043: why the print bar is dead, for the helper line under it. The URL alone cannot tell
  // "still building" from "the build failed", and those need different sentences.
  const [labelBuild, setLabelBuild] = useState(LABEL_BUILD.BUILDING);
  const [loading, setLoading] = useState(false);
  const [regenerateLabelLoading, setRegenerateLabelLoading] = useState(false);
  // Realtime per-label progress, fed by the labels_regenerated socket event.
  const [markItemsFilled, setMarkItemsFilled] = useState(false);
  const [printResult, setPrintResult] = useState(null);
  const [printResultOpen, setPrintResultOpen] = useState(false);
  // Fill mode's own summary, kept separate from printResult: that one also carries the
  // lot-divergence rows mark-as-printed reports, which a fill never produces.
  const [fillResult, setFillResult] = useState(null);
  const [fillResultOpen, setFillResultOpen] = useState(false);
  // POM-6010: the lot/NDC review step. `settledIds` is the printable subset the review returned —
  // blocked rows are excluded from both the PDF and the mark-as-printed call.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [settledIds, setSettledIds] = useState(null);
  const dispatch = useDispatch();
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const { enqueueSnackbar } = useSnackbar();

  const labelProgress = useLabelRegenerationProgress({
    // The run finished but nothing is tracking it — the user reloaded after sending
    // it to the queue, so the dialog and its ID set are gone. Tell them anyway; the whole point
    // of the server-side completion event is that it does not need the state we lost.
    onBackgroundComplete: ({ total, failed }) => {
      enqueueSnackbar(
        failed > 0
          ? `${total - failed} of ${total} labels regenerated. ${failed} failed.`
          : `${total} label(s) regenerated successfully`,
        { variant: failed > 0 ? 'warning' : 'success' }
      );
    },
  });

  const filledCheckboxClicked = () => setMarkItemsFilled(!markItemsFilled);

  /**
   * POM-6061: the selection narrowed to rows that are actually on screen.
   *
   * `labelItemIds` outlives the row set it was picked from — it is cleared when sortBy, filter or
   * the page CHANGE, not when the rows underneath it are replaced for some other reason (a list
   * response landing out of order, the "Bulk items have been updated" refresh reloading page 1,
   * the provider remounting and reseeding from locks the server still holds). Counting the badge
   * off the raw ids then advertised a selection with no checkbox ticked anywhere, and the dialogs
   * behind the button — which have always narrowed to the visible page — opened empty.
   *
   * Only on the Work Queue: every other entry point (Patient View, Order View, Batch Shipments)
   * has no work-queue page to intersect against, and `items` is empty there.
   */
  const selectionItemIds = onWorkQueue
    ? labelItemIds.filter((id) => some(items, { lineItemId: id }))
    : labelItemIds;

  /**
   * The items in a given id set that a "mark as Filled" would actually fill.
   *
   * Takes the ids rather than reading them from state because the two callers disagree about
   * which set is current: the render wants whatever has settled by now, while
   * handleGenerateBulkLabels is handed the new set in the same tick that stores it and would
   * otherwise decide off the previous one.
   */
  const fulfillmentItemsFor = (ids) =>
    filter(items, ({ lineItemStatus, lineItemId }) =>
      find(
        ids,
        (itemId) => itemId === lineItemId && lineItemStatus === lineItemEnum.PRODUCT_FULFILLMENT
      )
    );

  /**
   * The ids every action at the bottom of the dialog operates on.
   *
   * `settledIds` is the review's printable subset and is null until it settles, so this is the
   * whole selection on the standalone print path and on the review's first pass. Mark as Printed
   * and the review's Regenerate already narrowed to it individually; naming it once is what lets
   * the "Also mark N product(s) as Filled" label agree with them.
   */
  const actionableItemIds = settledIds ?? selectionItemIds;

  // Counted off the printable subset, not the selection: an item the review left out is not
  // printed, not marked printed and not filled, so promising to fill it overstated what the
  // button does — the bar read "1 of 2 labels will print" beside "Also mark 2 product(s) as
  // Filled".
  const needsFulfillmentItems = fulfillmentItemsFor(actionableItemIds);

  // The toolbar badge and its disabled rule, which are decided before the popup ever opens and
  // must not move once it has: `needsFulfillmentItems` narrows to the review's printable
  // subset, so counting the button off it would drop the badge every time a row is excluded.
  const selectionFulfillmentItems = fulfillmentItemsFor(selectionItemIds);

  const closeDialog = () => {
    setOpen(false);
    setReviewOpen(false);
    setSettledIds(null);
    setBulkLabelUrl(null);
    setLabelBuild(LABEL_BUILD.BUILDING);
    setMarkItemsFilled(false);
    setRegenerateLabelLoading(false);
    setPrintResult(null);
  };

  const handleGenerateBulkLabels = (idsToPrint = selectionItemIds) => {
    // POM-5884: default the "Also mark as Filled" checkbox OFF only when the inventory dispense
    // flow is ON — there, scanning is the authoritative fulfillment step, so bulk print must not
    // auto-mark items filled. When the dispense flow is OFF, keep the pre-existing behavior:
    // pre-check the box when there are fulfillment items, so bulk print fulfills exactly as it did
    // before. The checkbox remains the manual fallback either way.
    const shouldMarkItemsFilled =
      !EnableDispenseFlow &&
      onWorkQueue &&
      !isEmpty(fulfillmentItemsFor(idsToPrint)) &&
      hasAccess.manageProductFulfillmentStatus;
    setMarkItemsFilled(shouldMarkItemsFilled);
    setOpen(true);
    /**
     * POM-6043: the built PDF is stale the moment a new build starts, and nulling it here is what
     * holds Download, Print and Mark as Printed shut until the replacement lands.
     *
     * Without it the review dialog kept the PRE-COMMIT file one click away for the whole round
     * trip. By the time settle() runs after a commit, every other half of the lock has already
     * released — counts.changed is 0, settled is true, commitStage is null — so bulkLabelUrl is
     * the only thing still holding the bar, and it was pointing at the old label. Downloading it
     * is the mild outcome; Print sends a physically wrong label and Mark as Printed then records
     * it as done. The exclusion toggle rebuilds through here too, where the stale file holds the
     * wrong SET of labels.
     *
     * Same rule handleReviewRegenerate already follows below.
     */
    setBulkLabelUrl(null);
    setLabelBuild(LABEL_BUILD.BUILDING);
    generateBulkLabelsApi({
      requestLineItemIds: idsToPrint,
      ...(!isEmpty(inputFilter) &&
        isBulk &&
        !isBatchShipment && {
          filter: {
            ...inputFilter,
            ...(inputFilter?.needsByDate && {
              needsByDate:
                inputFilter.needsByDate && format(new Date(inputFilter.needsByDate), 'yyyy-MM-dd'),
            }),
            ...(inputFilter?.shipByDate && {
              shipByDate:
                inputFilter.shipByDate && format(new Date(inputFilter.shipByDate), 'yyyy-MM-dd'),
            }),
          },
        }),
      ...(isBulk && { isBulk }),
    })
      .then((url) => {
        setBulkLabelUrl(url);
        setLabelBuild(LABEL_BUILD.READY);
      })
      .catch((error) => {
        // Marks the bar rather than closing anything on the review path: setOpen governs only the
        // standalone dialog, so the review stays up — with Download and Print dead for want of a
        // URL, and the helper line naming Regenerate Label as the way back.
        setLabelBuild(LABEL_BUILD.FAILED);
        setOpen(false);
        dispatch(handleError({ error }));
      });
  };

  // Only the Work Queue bulk path gets the review step, and only with the inventory lookup ON.
  // Every other entry point (Patient View, Order View, Batch Shipments, Transfer Mode) and the
  // whole OFF path must behave exactly as before — the review query is never even issued.
  const needsLotNdcReview = isBulk && onWorkQueue && EnableInventoryLookup;

  const handlePrintLabelsClick = () => {
    if (needsLotNdcReview) {
      setReviewOpen(true);

      return;
    }
    // Fill mode with the review switched off keeps the plain confirmation it has always had:
    // there is no lot or NDC to review, and a fill has no use for a PDF.
    if (isFillMode) {
      setOpen(true);

      return;
    }
    handleGenerateBulkLabels();
  };

  const handleReviewSettled = ({ printableLineItemIds }) => {
    // Deliberately does NOT close the review dialog: it switches itself to the print phase, so the
    // user stays in one popup. Only the PDF fetch is kicked off here.
    setSettledIds(printableLineItemIds);

    /**
     * A batch where every row blocks settles on an EMPTY printable set, and that is the ordinary
     * case rather than an edge one: the exclusion checkbox defaults to ticked whenever anything
     * blocks, so four expired-lot rows leave nothing to print the moment the dialog opens.
     *
     * There is no PDF to ask for there. generateBulkLabels merges the labels of the ids it is
     * given, so an empty list could only ever come back as the error dialog QA hit on open --
     * before the user had touched anything.
     *
     * A terminal state of its own rather than FAILED: nothing was attempted, so the bar must not
     * offer Regenerate Label as the way back. The print controls need no extra guard, since they
     * already gate on `bulkLabelUrl` and it stays null.
     */
    if (isEmpty(printableLineItemIds)) {
      setBulkLabelUrl(null);
      setLabelBuild(LABEL_BUILD.NOTHING_TO_PRINT);

      return;
    }

    handleGenerateBulkLabels(printableLineItemIds);
  };

  /**
   * Fill mode's terminal action, in place of Mark as Printed.
   *
   * Fills exactly `needsFulfillmentItems` — the review's printable subset narrowed to items
   * actually awaiting fulfillment — so a row the user excluded is not filled either. Same rule
   * Mark as Printed follows, for the same reason: the popup's count line is what the user acted
   * against, and filling something it said would be left out contradicts it.
   *
   * The lock / unresolved-product filter is the belt-and-suspenders the standalone Mark as Filled
   * dialog has always applied. The row checkbox blocks these items (BulkLabelsTable's
   * isProductUnresolved) and core's PRODUCT_ID_UNRESOLVED / DEFAULT_NDC_UNRESOLVED checks remain
   * the actual guarantee, but selection state can be stale by the time of submit.
   */
  const handleBulkFill = () => {
    setLoading(true);

    const lockedKeySet = new Set(
      map(
        lockedInventoryItems,
        ({ ndc, fulfillingPharmacyId }) => `${ndc}::${fulfillingPharmacyId}`
      )
    );
    const rowDataMap = new Map(map(items, (item) => [item.lineItemId, item]));
    const fillableIds = map(needsFulfillmentItems, 'lineItemId').filter((id) => {
      const row = rowDataMap.get(id);
      const isLocked = lockedKeySet.has(`${row?.ndc}::${row?.fulfillingPharmacyId}`);
      const isUnresolvedProduct = EnableInventoryLookup && !row?.productId && !row?.ndc;

      return !isLocked && !isUnresolvedProduct;
    });

    if (isEmpty(fillableIds)) {
      enqueueSnackbar(
        'All selected items are locked for physical count or have unresolved product/NDC data',
        { variant: 'warning' }
      );
      setLoading(false);

      return undefined;
    }

    return bulkFillApi({ lineItemIds: fillableIds })
      .then((result) => {
        clearLabelItems();
        closeDialog();
        onSuccess();

        if (result.blockedCount === 0) {
          enqueueSnackbar(`Marked ${result.processedCount} product(s) as filled!`, {
            variant: 'success',
          });
        } else {
          setFillResult({
            successCount: result.processedCount,
            failedCount: result.blockedCount,
            results: result.results,
          });
          setFillResultOpen(true);
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to mark product(s) as filled!', { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleMarkAsPrinted = () => {
    // POM-5884: don't reset the "mark as Filled" checkbox here. It ran on submit-click, so the
    // box visibly unchecked while the request was still in flight (confusing), and on failure it
    // wiped the user's choice before a retry. closeDialog() already resets it on success, and the
    // payload below reads markItemsFilled from the closure, so the fill request is unaffected.
    setLoading(true);
    // Blocked rows the review excluded never printed, so they must not be marked printed either.
    const lineItemIdsToPrint = [...actionableItemIds];

    markLineItemsPrintedApi({
      lineItemIds: lineItemIdsToPrint,
      isBulkAction: isBulk,
      // Guarded on the count as well as the box: ticking it and THEN excluding the only
      // fulfillment item hides the checkbox without clearing the flag, and core fills whatever it
      // is told to when the flag is set.
      ...(markItemsFilled && !isEmpty(needsFulfillmentItems) && { markItemsFilled }),
    })
      .then((result) => {
        clearLabelItems();
        onSuccess();

        // POM-6010: a lot committed from the review is recorded but not reserved, so bulkFill can
        // allocate a different lot than the label already printed. Core reports that per item;
        // surface it rather than letting a mislabelled vial pass silently.
        const diverged = (result.results ?? []).filter((row) => row.lotDiverged);

        if (result.failedCount > 0 || diverged.length > 0) {
          closeDialog();
          setPrintResult(result);
          setPrintResultOpen(true);

          if (result.failedCount > 0) {
            enqueueSnackbar(`${result.failedCount} item(s) could not be processed.`, {
              variant: 'warning',
            });
          }
          if (diverged.length > 0) {
            enqueueSnackbar(
              `${diverged.length} item(s) were filled from a different lot than the printed label shows.`,
              { variant: 'warning' }
            );
          }
        } else {
          closeDialog();
          enqueueSnackbar(`${result.successCount} item(s) marked as printed successfully.`, {
            variant: 'success',
          });
        }
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Leave a queued regeneration running and get out of the way.
   *
   * Only offered once core has confirmed the work went to the queue. Confirm first: the progress
   * bar is still moving, so closing looks like cancelling. It is not — and the dialog says so
   * rather than leaving the user to guess whether they just aborted 200 labels.
   *
   * Tracking is deliberately NOT reset. This hook lives in the provider, above the dialog, so
   * the per-label events keep arriving and the completion message still fires when the last
   * label lands. Closing hides the progress bar; it does not stop anything.
   */
  const handleRunInBackground = () => {
    showInfoDialog({
      title: 'Regeneration in progress',
      // useInfoDialog renders `message` straight into DialogContent, so a node works here and
      // the warning treatment stays local instead of adding an icon prop to a hook six other
      // dialogs share. Static text on purpose: interpolating the live count made it churn.
      message: (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pt: 0.5, pb: 1 }}>
          <Box
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              // warning.main is the brand navy; warningColour is the amber the stale-label
              // alert in this same dialog already uses, so both warnings read as one voice.
              backgroundColor: (theme) =>
                alpha(theme.palette.warning.warningColour || theme.palette.warning.main, 0.18),
            }}
          >
            <WarningAmberIcon
              sx={{
                fontSize: 26,
                color: (theme) => theme.palette.warning.warningColour || theme.palette.warning.main,
              }}
            />
          </Box>
          <Box>
            <Typography variant='body1' sx={{ fontWeight: 600, mb: 0.5 }}>
              Label regeneration is still running in the background.
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Closing this dialog will not stop it. Are you sure you want to close?
            </Typography>
          </Box>
        </Box>
      ),
      callback: closeDialog,
      cancelable: true,
      confirmButtonText: 'Close anyway',
    });
  };

  const handleRegenerateLabel = () => {
    showInfoDialog({
      title: 'Confirm Regenerate',
      message:
        labelItemIds.length > 1
          ? `Are you sure you want to regenerate ${labelItemIds.length} labels?`
          : `Are you sure you want to regenerate the label?`,
      callback: () => {
        setRegenerateLabelLoading(true);

        // The mutation stays synchronous, but core publishes one socket event per
        // label as it commits. Tracking starts BEFORE the call because the first labels finish
        // well before the request returns — registering afterwards would drop those events.
        const finish = ({ completed, total, failed = 0, timedOut = false } = {}) => {
          setRegenerateLabelLoading(false);
          labelProgress.reset();

          // The server-side completion event knows what actually failed, which the per-label
          // count never can — a label that errors simply never reports.
          if (failed > 0) {
            enqueueSnackbar(`${completed} of ${total} labels regenerated. ${failed} failed.`, {
              variant: 'warning',
            });

            closeDialog();
            onSuccess();

            return;
          }

          // A timeout is not a success. Some labels may be ineligible and so never report, or
          // the socket may have dropped — either way we stopped hearing before the count was
          // met and must not claim everything finished. The work itself is unaffected; the
          // row-level regeneration status is the source of truth from here.
          if (timedOut && completed < total) {
            enqueueSnackbar(
              `${completed} of ${total} labels confirmed. The rest may still be processing — refresh to check their status.`,
              { variant: 'warning' }
            );
          } else {
            enqueueSnackbar(
              total > 1
                ? `${completed ?? total} of ${total} label(s) regenerated successfully`
                : 'Label regenerated successfully',
              { variant: 'success' }
            );
          }

          closeDialog();
          onSuccess();
        };

        labelProgress.startTracking({
          requestLineItemIds: labelItemIds,
          // Every label reported in — close on the last one rather than on the HTTP response.
          onComplete: ({ completed, total, failed }) => finish({ completed, total, failed }),
          // No events, or not all of them: the socket may be down, this screen may not have
          // joined the room, or a label may have been skipped as ineligible and so never
          // reported. Fall back to the pre-realtime behaviour rather than hanging.
          onTimeout: ({ completed, total }) => finish({ completed, total, timedOut: true }),
        });

        regenerateLabelsApi(labelItemIds)
          .then(() => {
            // What the mutation resolving means now depends on which branch core took.
            //
            // Below the threshold it ran the work inside the request, so `true` means done —
            // and if realtime is unavailable there is nothing left to wait for, so finish here
            // rather than sitting on the timeout.
            //
            // Above it, core queued the work and returned in under a second. `true` only means
            // accepted. Finishing here would close the dialog before a single label existed, so
            // we hold and let the socket drive: labels_regeneration_queued has already set
            // isQueued and extended the window, and the per-label events tick the bar from
            // there. Without realtime a queued run cannot be followed at all — say so plainly
            // instead of claiming a success we cannot observe.
            if (labelProgress.isRealtimeAvailable) {
              return;
            }

            if (labelProgress.isQueued) {
              labelProgress.reset();
              setRegenerateLabelLoading(false);
              enqueueSnackbar(
                `${labelItemIds.length} labels queued for regeneration. They will continue in the background.`,
                { variant: 'info' }
              );
              closeDialog();
              onSuccess();

              return;
            }

            finish({ completed: labelItemIds.length, total: labelItemIds.length });
          })
          .catch(() => {
            labelProgress.reset();
            setRegenerateLabelLoading(false);
            enqueueSnackbar('An unexpected error occurred during label regeneration.', {
              variant: 'error',
            });
          });
      },
      cancelable: true,
      confirmButtonText: 'Yes',
    });
  };

  /**
   * Regenerate from inside the review dialog.
   *
   * Deliberately not handleRegenerateLabel: that one closes the dialog and reloads the page, which
   * is right for the standalone popup but would throw away the review the user is standing in --
   * their filters, and their acknowledgement of the excluded rows. Here the labels are rebuilt and
   * the PDF is fetched again in place, so the bar comes back live on the new labels.
   *
   * It regenerates only the printable subset, for the same reason mark-as-printed does: a row the
   * review excluded is not printing, so rebuilding its label would be work with no output.
   */
  const handleReviewRegenerate = () => {
    const idsToRegenerate = [...actionableItemIds];
    if (isEmpty(idsToRegenerate)) return;

    showInfoDialog({
      title: 'Confirm Regenerate',
      message: `Are you sure you want to regenerate ${idsToRegenerate.length} label(s)?`,
      callback: () => {
        setRegenerateLabelLoading(true);
        // The built PDF is now stale, and nulling it is what disables Download and Print while the
        // replacement is fetched -- otherwise the old file stays one click away.
        setBulkLabelUrl(null);
        setLabelBuild(LABEL_BUILD.BUILDING);

        /**
         * The PDF is built from whatever the labels say at the moment it is requested, so this
         * must not run until they have actually been rebuilt.
         *
         * That is not the same as the mutation resolving. Above BULK_REGENERATE_ASYNC_THRESHOLD
         * core queues the work and returns `true` in under a second with nothing rendered — so
         * rebuilding the PDF there produced a file full of the PREVIOUS lot, reported as a
         * success. Same reasoning as handleRegenerateLabel above; only the ending differs,
         * because this one stays in the review instead of closing it.
         */
        const finish = ({ completed = 0, total = 0, failed = 0, timedOut = false } = {}) => {
          labelProgress.reset();
          setRegenerateLabelLoading(false);

          if (failed > 0) {
            enqueueSnackbar(`${completed} of ${total} labels regenerated. ${failed} failed.`, {
              variant: 'warning',
            });
          } else if (timedOut && completed < total) {
            // Said plainly rather than swallowed: the PDF below is built from whatever exists
            // now, and some of it may still be the previous lot.
            enqueueSnackbar(
              `${completed} of ${total} labels confirmed. The rest may still be processing — regenerate again if the printed lot looks wrong.`,
              { variant: 'warning' }
            );
          } else {
            enqueueSnackbar('Label regenerated successfully', { variant: 'success' });
          }

          handleGenerateBulkLabels(idsToRegenerate);
        };

        // Before the call, not after: the first labels finish well before the request returns.
        labelProgress.startTracking({
          requestLineItemIds: idsToRegenerate,
          onComplete: ({ completed, total, failed }) => finish({ completed, total, failed }),
          onTimeout: ({ completed, total }) => finish({ completed, total, timedOut: true }),
        });

        regenerateLabelsApi(idsToRegenerate)
          .then(() => {
            // With realtime the socket decides when this is done. Without it there is nothing to
            // wait for on the synchronous branch, so settle here rather than on the timeout.
            if (labelProgress.isRealtimeAvailable) return;

            finish({ completed: idsToRegenerate.length, total: idsToRegenerate.length });
          })
          .catch(() => {
            labelProgress.reset();
            setRegenerateLabelLoading(false);
            enqueueSnackbar('An unexpected error occurred during label regeneration.', {
              variant: 'error',
            });
          });
      },
      cancelable: true,
      confirmButtonText: 'Yes',
    });
  };

  const handlePrintLabel = async () => {
    if (!bulkLabelUrl) return;

    const startJSPMWithTimeout = () => {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          if (JSPrintManager.websocket_status !== WSStatus.Open) {
            reject(new Error('Timeout'));
          }
        }, 1500);

        JSPrintManager.start()
          .then(() => {
            if (JSPrintManager.websocket_status === WSStatus.Open) {
              clearTimeout(timeout);
              resolve();
            } else {
              clearTimeout(timeout);
              reject(new Error('Handshake failed'));
            }
          })
          .catch((err) => {
            clearTimeout(timeout);
            reject(err);
          });
      });
    };

    if (JSPrintManager.websocket_status !== WSStatus.Open) {
      try {
        enqueueSnackbar({ variant: 'info', message: 'Connecting to printer client...' });
        JSPrintManager.auto_reconnect = false;
        await startJSPMWithTimeout();
      } catch (e) {
        enqueueSnackbar({
          variant: 'error',
          message: 'Printer Client Not Installed or Not Running',
        });
        return;
      }
    }

    try {
      const printers = await JSPrintManager.getPrinters();
      if (!printers || printers.length === 0) {
        enqueueSnackbar({ variant: 'error', message: 'Default Printer not configured' });
        return;
      }

      const cpj = new ClientPrintJob();
      cpj.clientPrinter = new DefaultPrinter();

      const myFile = new PrintFilePDF(
        bulkLabelUrl,
        FileSourceType.ExternalURL,
        `${Date.now()}.pdf`,
        1
      );

      myFile.pageSizing = Sizing.None;
      myFile.usePaperSize = true;
      myFile.printCentering = true;

      cpj.files.push(myFile);
      cpj.sendToClient();
      enqueueSnackbar({ variant: 'info', message: 'Print job sent to printer.' });
    } catch (error) {
      enqueueSnackbar({ variant: 'error', message: 'JSPM Error: Could not send to printer' });
    }
  };

  const displayCount = isTransferMode
    ? 0
    : (isFillMode ? selectionFulfillmentItems : selectionItemIds).length;
  const isDisabled =
    disabled ||
    (isFillMode
      ? isTransferMode ||
        isEmpty(selectionFulfillmentItems) ||
        !hasAccess.manageProductFulfillmentStatus
      : isTransferMode || isEmpty(selectionItemIds) || !hasAccess.generateBulkLabels);

  const isSelectedItemsShipped = labelItemIds.some((labelItemId) => {
    const selectedItem = find(allLineItems, ({ lineItemId }) => lineItemId === labelItemId);
    return selectedItem?.processingState === lineItemEnum.SHIPPED;
  });

  const allPagedItems = Object.values(pages).flat();

  const hasRegeneratingLabels = labelItemIds.some((id) => {
    const item =
      find(contextLineItems, { lineItemId: id }) ||
      find(allPagedItems, { lineItemId: id }) ||
      find(allLineItems, { lineItemId: id });
    return item?.labelRegenerationStatus === 'REGENERATING';
  });

  useEffect(() => {
    JSPrintManager.auto_reconnect = false;

    return () => {
      if (JSPrintManager.WS && JSPrintManager.websocket_status !== WSStatus.Closed) {
        JSPrintManager?.stop();
      }
    };
  }, []);

  // The standalone print dialog, unchanged: every non-bulk entry point still gets the centred
  // Download/Print pair under the animation. POM-6010's review dialog arranges the same handlers
  // into a bottom bar instead (see reviewPrintNotices / renderReviewPrintActions below) — only the
  // layout differs, no print or JSPM logic is duplicated.
  const printPhaseContent = (
    <>
      {!bulkLabelUrl && <LoadingModule height='240px' />}
      {bulkLabelUrl && (
        <Grid
          container
          direction='column'
          justifyContent='center'
          alignItems='center'
          sx={{ p: 6 }}
        >
          <Lottie animationData={Download} loop={false} style={{ height: 200, padding: 10 }} />
          <Box>
            <Grid container>
              <Button
                component={Link}
                href={bulkLabelUrl}
                target='_blank'
                variant='outlined'
                color='secondary'
                sx={{ width: 120, mr: 2 }}
              >
                Download
              </Button>

              <Button
                onClick={handlePrintLabel}
                variant='contained'
                color='primary'
                sx={{ width: 120 }}
              >
                Print
              </Button>
            </Grid>
          </Box>
        </Grid>
      )}
      <Alert severity='info' sx={{ ml: 2, mt: 2, fontSize: '0.8rem' }}>
        Not seeing the latest updates on the label? Please try regenerating.
      </Alert>
      {hasRegeneratingLabels && (
        <Alert
          severity='warning'
          variant='filled'
          icon={<WarningAmberIcon fontSize='large' />}
          sx={{
            ml: 2,
            mt: 1,
            fontSize: '0.85rem',
            fontWeight: 'bold',
            alignItems: 'center',
            backgroundColor: (theme) =>
              theme.palette.warning.warningColour || theme.palette.warning.main,
            color: 'text.primary',
            '& .MuiAlert-icon': {
              color: 'text.primary',
            },
          }}
        >
          {REGENERATING_LABELS_MESSAGE}
        </Alert>
      )}

      {/* Live regeneration progress, one label at a time. Determinate bar because
          we know the total up front — the count is the number of items submitted. */}
      {labelProgress.isTracking && labelProgress.total > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              mb: 0.5,
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              Regenerating labels…
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {labelProgress.completedCount} of {labelProgress.total}
            </Typography>
          </Box>
          <LinearProgress variant='determinate' value={labelProgress.percent} />
        </Box>
      )}
    </>
  );

  const printPhaseActions = (
    <>
      {onWorkQueue &&
        !isEmpty(needsFulfillmentItems) &&
        hasAccess.manageProductFulfillmentStatus && (
          <FormControlLabel
            label={`Also mark ${needsFulfillmentItems.length} product(s) as Filled`}
            checked={markItemsFilled}
            control={
              <Checkbox
                onChange={filledCheckboxClicked}
                // The other half of the lock below: no toggling the fill while a
                // regeneration or a print is actually running.
                disabled={regenerateLabelLoading || loading}
              />
            }
            sx={{
              mr: 'auto',
              flexShrink: 0,
              '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' },
            }}
          />
        )}
      {/* Previously hidden on the Work Queue, so a bulk selection could not be
          regenerated at all. Same gating as elsewhere — never for shipped items.
          No count badge here: the dialog already states the selection size, and the badge
          belongs on the toolbar buttons that open it. */}
      <Tooltip
        title={markItemsFilled ? REGENERATE_LOCKED_BY_FILL_MESSAGE : ''}
        arrow
        placement='top'
      >
        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
          <LoadingButton
            variant='contained'
            loading={regenerateLabelLoading}
            disabled={
              regenerateLabelLoading ||
              loading ||
              isSelectedItemsShipped ||
              isEmpty(labelItemIds) ||
              markItemsFilled
            }
            onClick={handleRegenerateLabel}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Regenerate Label
          </LoadingButton>
        </span>
      </Tooltip>
      <LoadingButton
        variant='contained'
        loading={loading}
        disabled={!bulkLabelUrl || !hasAccess.markLineItemsPrinted || regenerateLabelLoading}
        onClick={handleMarkAsPrinted}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        Mark as Printed
      </LoadingButton>
      {/* A queued regeneration outlives this dialog, so Close is re-enabled for it — but
          keeps its label, because it still does what it says. What changes is that it warns
          first, since a moving progress bar makes closing look like cancelling.
          The synchronous branch stays locked while the request is open: there the work
          really is tied to the request, and closing would strand the user with no way back
          to the progress. */}
      <Button
        onClick={labelProgress.isQueued ? handleRunInBackground : closeDialog}
        variant='outlined'
        color='secondary'
        disabled={loading || (regenerateLabelLoading && !labelProgress.isQueued)}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        Close
      </Button>
    </>
  );

  // POM-6010: the review dialog puts the print controls in its own bottom bar, so it needs the
  // same handlers arranged differently — notices in the body, buttons in the action row. Nothing
  // is duplicated but the layout; every onClick below is the same handler the standalone dialog
  // uses. `locked` is the review's half of the enablement rule (is the batch settled), and the
  // per-button half (is the PDF built, may this user mark printed) stays here.
  const reviewPrintNotices = hasRegeneratingLabels ? (
    <Alert
      severity='warning'
      variant='filled'
      icon={<WarningAmberIcon fontSize='large' />}
      sx={{
        mt: 1,
        fontSize: '0.85rem',
        fontWeight: 'bold',
        alignItems: 'center',
        backgroundColor: (theme) =>
          theme.palette.warning.warningColour || theme.palette.warning.main,
        color: 'text.primary',
        '& .MuiAlert-icon': { color: 'text.primary' },
      }}
    >
      {REGENERATING_LABELS_MESSAGE}
    </Alert>
  ) : null;

  /**
   * The regenerate button, with the prompt that asks for it.
   *
   * POM-6534: the prompt is the button's tooltip rather than a box beside it, so the bottom bar
   * keeps its width for the print controls. The span wrapper keeps it readable while the button is
   * disabled.
   *
   * The button reads `busy` rather than the print lock: rebuilding labels does not need a built
   * PDF (it replaces one) and does not care whether the excluded rows have been acknowledged, only
   * that nothing is in flight. While an edit waits on a commit the tooltip says that instead, since
   * "try regenerating" would point at a button that cannot be pressed.
   */
  const renderReviewRegenerateNotice = ({ busy, lockedHint }) => (
    <DisabledButtonTooltip
      title={lockedHint || 'Not seeing the latest updates on the label? Please try regenerating.'}
    >
      <LoadingButton
        variant='contained'
        loading={regenerateLabelLoading}
        disabled={
          busy ||
          regenerateLabelLoading ||
          loading ||
          isSelectedItemsShipped ||
          // Nothing printable means nothing for a rebuild to produce: handleReviewRegenerate
          // regenerates `actionableItemIds` and returns early when that is empty, so the button
          // was live but inert on the one screen that most needs a way forward.
          isEmpty(actionableItemIds)
        }
        onClick={handleReviewRegenerate}
      >
        Regenerate Label
      </LoadingButton>
    </DisabledButtonTooltip>
  );

  /**
   * `lockedHint` is the review's reason the bar is locked while an edit waits on a commit, or ''.
   * It rides as a tooltip on each locked control, because a disabled button otherwise says nothing.
   */
  const renderReviewPrintActions = ({ locked, lockedHint }) => {
    // A real <button disabled> rather than the standalone dialog's anchor: an anchor cannot be
    // disabled, and this bar renders in its locked state most of the time.
    const notReady = locked || !bulkLabelUrl || regenerateLabelLoading;

    return (
      <>
        <DisabledButtonTooltip title={lockedHint}>
          <Button
            variant='outlined'
            color='secondary'
            disabled={notReady}
            onClick={() => window.open(bulkLabelUrl, '_blank', 'noopener')}
          >
            Download
          </Button>
        </DisabledButtonTooltip>
        <DisabledButtonTooltip title={lockedHint}>
          <Button
            variant='contained'
            color='primary'
            disabled={notReady}
            onClick={handlePrintLabel}
          >
            Print
          </Button>
        </DisabledButtonTooltip>
        {/* Print mode's pair: the opt-in fill and the mark-as-printed it rides along with. Fill
            mode replaces both with the single button below — it came here to fill, so an "also
            fill" checkbox beside a Mark as Printed it never asked for is two controls for one
            action. Every other control in this bar is shared. */}
        {!isFillMode &&
          onWorkQueue &&
          !isEmpty(needsFulfillmentItems) &&
          hasAccess.manageProductFulfillmentStatus && (
            <FormControlLabel
              label={`Also mark ${needsFulfillmentItems.length} product(s) as Filled`}
              checked={markItemsFilled}
              disabled={notReady}
              control={<Checkbox onChange={filledCheckboxClicked} />}
            />
          )}
        {/* Regenerate lives in renderReviewRegenerateNotice, with the prompt that asks for it. */}
        <DisabledButtonTooltip title={lockedHint}>
          {isFillMode ? (
            // Gated on the same notReady as Mark as Printed, deliberately — including the built
            // PDF. The point of this screen is that what gets filled is what the operator verified
            // on the label, so filling before the labels are current would skip the verification
            // the popup exists to provide. A failed build is recoverable from the notice beside
            // these controls, and every role that may fill may also build labels, so this cannot
            // strand a user.
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={notReady || !hasAccess.manageProductFulfillmentStatus}
              onClick={handleBulkFill}
            >
              Mark as Filled
            </LoadingButton>
          ) : (
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={notReady || !hasAccess.markLineItemsPrinted}
              onClick={handleMarkAsPrinted}
            >
              Mark as Printed
            </LoadingButton>
          )}
        </DisabledButtonTooltip>
      </>
    );
  };

  return (
    <>
      <InfoDialog />
      <Tooltip
        title={hasRegeneratingLabels ? REGENERATING_LABELS_MESSAGE : ''}
        arrow
        placement='top'
      >
        <Badge color='error' badgeContent={displayCount} sx={{ mx: 1 }}>
          <Button
            disabled={isDisabled}
            variant='contained'
            color='primary'
            onClick={handlePrintLabelsClick}
            startIcon={
              hasRegeneratingLabels ? (
                <WarningAmberIcon
                  sx={{
                    color: (theme) =>
                      theme.palette.warning.warningColour || theme.palette.warning.main,
                  }}
                />
              ) : undefined
            }
          >
            {isFillMode ? 'Mark as Filled' : 'Print Labels'}
          </Button>
        </Badge>
      </Tooltip>

      {reviewOpen && (
        <BulkLotNdcReviewDialog
          open
          requestLineItemIds={selectionItemIds}
          onSettled={handleReviewSettled}
          onClose={closeDialog}
          printNotices={reviewPrintNotices}
          renderPrintNotice={renderReviewRegenerateNotice}
          renderPrintActions={renderReviewPrintActions}
          // The regeneration bar the standalone dialog renders inline (see printPhaseContent) has
          // no render site on this path — the review dialog replaces that whole dialog. Handing
          // the tracking state down lets it draw the count in its own progress panel instead of
          // the run happening invisibly.
          printProgress={labelProgress}
          labelBuild={labelBuild}
        />
      )}

      {/* Non-bulk entry points keep the standalone print dialog exactly as before, and fill mode
          keeps the plain confirmation it had before the review popup existed. */}
      {!needsLotNdcReview && (
        <>
          {isFillMode && (
            <Dialog
              open={open}
              maxWidth='sm'
              fullWidth
              aria-labelledby='WorkQueue-bulkLabels-BatchFill-header'
            >
              <DialogTitle id='WorkQueue-bulkLabels-BatchFill-header'>Fill Product</DialogTitle>
              <DialogContent>
                <Typography>
                  {`Are you sure you want to mark ${needsFulfillmentItems.length} product(s) as filled? `}
                </Typography>
              </DialogContent>
              <DialogActions>
                <LoadingButton
                  variant='contained'
                  loading={loading}
                  disabled={!hasAccess.manageProductFulfillmentStatus}
                  onClick={handleBulkFill}
                >
                  Mark as Filled
                </LoadingButton>
                <Button
                  onClick={closeDialog}
                  variant='outlined'
                  color='secondary'
                  disabled={loading}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          )}
          {!isFillMode && (
            <Dialog
              open={open}
              // 'sm' was sized for three controls. With Regenerate Label added, the row ran
              // out of width and both it and the "Also mark N product(s) as Filled" label wrapped.
              maxWidth='md'
              fullWidth
              aria-labelledby='WorkQueue-bulkLabel-download-header'
            >
              <DialogTitle id='WorkQueue-bulkLabel-download-header'>
                Bulk Labels Download
              </DialogTitle>
              <DialogContent>{printPhaseContent}</DialogContent>
              {/* The checkbox is pushed left and the buttons kept together on the right, so
                  adding a control does not squeeze the others into wrapping. */}
              <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'nowrap' }}>
                {printPhaseActions}
              </DialogActions>
            </Dialog>
          )}
        </>
      )}

      {/* Fill Summary, for whichever path the fill ran on. */}
      {isFillMode && (
        <Dialog
          open={fillResultOpen}
          maxWidth='sm'
          fullWidth
          aria-labelledby='WorkQueue-fillResult-header'
        >
          <DialogTitle id='WorkQueue-fillResult-header'>Fill Summary</DialogTitle>
          <DialogContent>
            <PrintResultSummary printResult={fillResult} isError={fillResult?.successCount === 0} />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setFillResultOpen(false);
                setFillResult(null);
              }}
              variant='outlined'
              color='secondary'
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Print & Fill Summary Dialog */}
      <Dialog
        open={printResultOpen}
        maxWidth='sm'
        fullWidth
        aria-labelledby='WorkQueue-printResult-header'
      >
        <DialogTitle id='WorkQueue-printResult-header'>Print & Fill Summary</DialogTitle>
        <DialogContent>
          <PrintResultSummary printResult={printResult} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPrintResultOpen(false);
              setPrintResult(null);
            }}
            variant='outlined'
            color='secondary'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * The two bulk toolbar buttons, one component apart.
 *
 * Print Labels keeps its public API untouched — every existing call site (Order View, Patient View,
 * Batch Shipments, the Work Queue) passes exactly what it passed before.
 *
 * Mark as Filled is fixed to the Work Queue bulk path because that is the only place it is
 * rendered, and those are the flags that put it on the review path: `isBulk && onWorkQueue &&
 * EnableInventoryLookup`. With the lookup switched off it falls through to its own plain Fill
 * Product confirmation, exactly as before.
 */
const PrintLabelButton = ({
  onSuccess,
  onWorkQueue,
  isBulk,
  isBatchShipment,
  isTransferMode,
  disabled,
}) => (
  <BulkLabelActionButton
    mode={BULK_ACTION_MODE.PRINT}
    onSuccess={onSuccess}
    onWorkQueue={onWorkQueue}
    isBulk={isBulk}
    isBatchShipment={isBatchShipment}
    isTransferMode={isTransferMode}
    disabled={disabled}
  />
);

const MarkAsFilledButton = ({ onSuccess, isTransferMode }) => (
  <BulkLabelActionButton
    mode={BULK_ACTION_MODE.FILL}
    onSuccess={onSuccess}
    isTransferMode={isTransferMode}
    onWorkQueue
    isBulk
  />
);

const SelectAllCheckBox = ({ allLineItemIds, isDisabled }) => {
  const {
    labelItemIds,
    setLabelItemIds,
    isConnected,
    debouncedSyncLockState,
    setUserUnselectedItems,
    labelItemIdsRef,
  } = usePrintLabelContext();

  const hasSelectableIds = !isEmpty(allLineItemIds);
  const includeSomeIds =
    hasSelectableIds && some(allLineItemIds, (lineItemId) => includes(labelItemIds, lineItemId));
  const allIdsChecked =
    hasSelectableIds && every(allLineItemIds, (lineItemId) => includes(labelItemIds, lineItemId));

  const handleClickEvent = (event) => {
    const { checked } = event.target;

    setLabelItemIds((currentLabelItemIds) => {
      let updatedIds;

      if (checked) {
        updatedIds = uniq([...allLineItemIds, ...currentLabelItemIds]);

        setUserUnselectedItems((prevUnselected) => {
          const newSet = new Set(prevUnselected);
          allLineItemIds.forEach((id) => newSet.delete(id));
          return newSet;
        });
      } else {
        const itemsToDeselect = allLineItemIds.filter((lineItemId) =>
          includes(currentLabelItemIds, lineItemId)
        );

        updatedIds = without(currentLabelItemIds, ...itemsToDeselect);

        setUserUnselectedItems((prevUnselected) => {
          const newSet = new Set(prevUnselected);
          itemsToDeselect.forEach((id) => newSet.add(id));
          return newSet;
        });
      }

      labelItemIdsRef.current = updatedIds;

      return updatedIds;
    });

    if (isConnected) {
      debouncedSyncLockState();
    }
  };

  return (
    <span>
      <Checkbox
        size='small'
        onClick={handleClickEvent}
        indeterminate={!allIdsChecked && includeSomeIds}
        checked={allIdsChecked}
        inputProps={{ 'aria-label': `select all line items to be printed` }}
        disabled={isDisabled || !hasSelectableIds}
      />
    </span>
  );
};

SelectAllCheckBox.propTypes = {
  allLineItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  isDisabled: PropTypes.bool,
};

SelectAllCheckBox.defaultProps = {
  isDisabled: false,
};

BulkLabelActionButton.propTypes = {
  mode: PropTypes.oneOf(Object.values(BULK_ACTION_MODE)).isRequired,
  onSuccess: PropTypes.func,
  onWorkQueue: PropTypes.bool,
  isBulk: PropTypes.bool,
  isBatchShipment: PropTypes.bool,
  isTransferMode: PropTypes.bool,
  disabled: PropTypes.bool,
};

BulkLabelActionButton.defaultProps = {
  onSuccess: noop,
  onWorkQueue: false,
  isBulk: false,
  isBatchShipment: false,
  isTransferMode: false,
  disabled: false,
};

PrintLabelButton.propTypes = {
  onSuccess: PropTypes.func,
  onWorkQueue: PropTypes.bool,
  isBulk: PropTypes.bool,
  isBatchShipment: PropTypes.bool,
  isTransferMode: PropTypes.bool,
  disabled: PropTypes.bool,
};

PrintLabelButton.defaultProps = {
  onSuccess: noop,
  onWorkQueue: false,
  isBulk: false,
  isBatchShipment: false,
  isTransferMode: false,
  disabled: false,
};

MarkAsFilledButton.propTypes = {
  onSuccess: PropTypes.func,
  isTransferMode: PropTypes.bool,
};

MarkAsFilledButton.defaultProps = {
  onSuccess: noop,
  isTransferMode: false,
};

export default usePrintLabelContext;
export {
  MarkAsFilledButton,
  PrintLabelButton,
  PrintLabelCheckBox,
  PrintLabelProvider,
  SelectAllCheckBox,
};
